import * as EthAddress from 'ethers/address'

import { SupportedNetwork } from '@blade/blockchain-types'
import { Schema as Schema_ } from '@blade/std-lib'
import { isSolanaAddress } from '@root/entities/network/model/solana/solana-address'
import { PairAddress } from '@root/entities/pair/model/PairAddress'
import { TonUtil } from '@root/shared/lib/ton'

import type { Address } from '@root/entities/address/Address'
import type { TxHash } from '@root/entities/transaction/model/tx-hash'
import type { UserApi } from '@root/shared/api'

export enum Network {
  Ethereum = 'ethereum',
  Base = 'base',
  Solana = 'solana',
  Ton = 'ton',
  Bsc = 'bsc',
}

export const Ethereum = Network.Ethereum
export const Base = Network.Base
export const Solana = Network.Solana
export const Ton = Network.Ton
export const Bsc = Network.Bsc

export const Schema = Schema_.Enum(Network)

export type ByNetwork<T> = Record<Network, T>
export const networks: Array<Network> = [Ethereum, Base, Solana, Ton, Bsc]
export type EvmNetwork = Network.Ethereum | Network.Base | Network.Bsc

const networkToDisplayStringMap: Record<Network, string> = {
  [Ethereum]: 'Eth',
  [Solana]: 'Sol',
  [Ton]: 'Ton',
  [Base]: 'Base',
  [Bsc]: 'Bsc',
}
export const toDisplayString = (network: Network) => {
  return networkToDisplayStringMap[network]
}
const networkToDtoMap: Record<Network, UserApi.NetworkDto> = {
  [Ethereum]: 'eth',
  [Solana]: 'sol',
  [Ton]: 'ton',
  [Base]: 'base',
  [Bsc]: 'bsc',
}
export const toDto = (network: Network) => {
  return networkToDtoMap[network]
}

const dtoToNetworkMap: Record<string, Network> = {
  eth: Ethereum,
  sol: Solana,
  ton: Ton,
  base: Base,
  bsc: Bsc,
}

export const fromDto = (dto: string) => {
  const network = dtoToNetworkMap[dto]
  if (network === undefined) {
    if (dto === 'evm') {
      return Ethereum
    }
    return Solana
  }
  return network
}

/**
 * A network named in a url, or nothing.
 *
 * Both spellings arrive: our own links carry `solana`, while a link from the
 * API, the bot or somebody typing carries `sol`. Anything else is not a
 * network of ours, and saying so is the point — `fromDto` answers Solana for
 * whatever it does not know, and a caller that stores that answer stores a
 * network the person never asked for. Worse, a caller that stores the raw
 * segment stores a value no lookup here has a key for.
 */
export const fromPath = (segment: string): Network | undefined => {
  const named = networks.find((network) => network === segment)

  return named ?? dtoToNetworkMap[segment]
}
const isEvmNetworkMap: Record<Network, boolean> = {
  [Ethereum]: true,
  [Base]: true,
  [Bsc]: true,
  [Solana]: false,
  [Ton]: false,
}
export const isEvmNetwork = (network: Network): network is EvmNetwork => {
  return isEvmNetworkMap[network]
}

const toSupportedNetworkMap: Record<Network, SupportedNetwork> = {
  [Ethereum]: SupportedNetwork.Eth,
  [Solana]: SupportedNetwork.Sol,
  [Ton]: SupportedNetwork.Ton,
  [Base]: SupportedNetwork.Base,
  [Bsc]: SupportedNetwork.Bsc,
}

export const toSupportedNetwork = (network: Network) => {
  return toSupportedNetworkMap[network]
}

const fromSupportedNetworkMap: Record<SupportedNetwork, Network> = {
  [SupportedNetwork.Eth]: Ethereum,
  [SupportedNetwork.Sol]: Solana,
  [SupportedNetwork.Ton]: Ton,
  [SupportedNetwork.Base]: Base,
  [SupportedNetwork.Bsc]: Bsc,
}
export const fromSupportedNetwork = (network: SupportedNetwork) => {
  return fromSupportedNetworkMap[network]
}

export const parse = (raw: unknown): Network | undefined => {
  return networks.find((it) => it === raw)
}

const validateAddressByNetwork: Record<Network, (address: string) => boolean> =
  {
    [Solana]: isSolanaAddress,
    [Ton]: (address) => TonUtil.isAddressValid(address),
    [Ethereum]: (address) => EthAddress.isAddress(address),
    [Base]: (address) => EthAddress.isAddress(address),
    [Bsc]: (address) => EthAddress.isAddress(address),
  }

export const validateAddress = (network: Network) => (address: string) => {
  return validateAddressByNetwork[network](address)
}

const explorerTransactionLinkByNetwork: Record<
  Network,
  (txId: TxHash) => string
> = {
  [Ethereum]: (txId) => `https://etherscan.io/tx/${txId}`,
  [Solana]: (txId) => `https://solscan.io/tx/${txId}`,
  [Ton]: (txId) => `https://tonscan.org/tx/${txId}`,
  [Base]: (txId) => `https://basescan.org/tx/${txId}`,
  [Bsc]: (txId) => `https://bscscan.com/tx/${txId}`,
}

export const makeExplorerTransactionLink = (
  network: Network,
  txId: TxHash,
): string => {
  return explorerTransactionLinkByNetwork[network](txId)
}

const networkToExplorerNameMap: Record<Network, string> = {
  [Ethereum]: 'Etherscan',
  [Solana]: 'Solscan',
  [Ton]: 'Tonscan',
  [Base]: 'Basescan',
  [Bsc]: 'bscscan',
}

export const networkToExplorerName = (network: Network): string => {
  return networkToExplorerNameMap[network]
}

const explorerContractLinkByNetwork: Record<
  Network,
  (contractAddress: Address) => string
> = {
  [Ethereum]: (contractAddress) =>
    `https://etherscan.io/address/${contractAddress}`,
  [Solana]: (contractAddress) =>
    `https://solscan.io/account/${contractAddress}`,
  [Ton]: (contractAddress) => `https://tonscan.org/address/${contractAddress}`,
  [Base]: (contractAddress) =>
    `https://basescan.org/address/${contractAddress}`,
  [Bsc]: (contractAddress) => `https://bscscan.com/address/${contractAddress}`,
}

export const makeExplorerContractLink = (
  network: Network,
  contractAddress: Address,
): string => {
  return explorerContractLinkByNetwork[network](contractAddress)
}

const explorerHoldersLinkByNetwork: Record<
  Network,
  (contractAddress: Address) => string
> = {
  [Ethereum]: (contractAddress) =>
    `https://etherscan.io/token/${contractAddress}#balances`,
  [Solana]: (contractAddress) =>
    `https://solscan.io/token/${contractAddress}#holders`,
  [Ton]: (contractAddress) => `https://tonscan.org/address/${contractAddress}`,
  [Base]: (contractAddress) =>
    `https://basescan.org/address/${contractAddress}`,
  [Bsc]: (contractAddress) => `https://bscscan.com/address/${contractAddress}`,
}

export const makeExplorerHoldersLink = (
  network: Network,
  contractAddress: Address,
): string => {
  return explorerHoldersLinkByNetwork[network](contractAddress)
}

export const byNetworkRecord = <T>(
  fn: (network: Network) => T,
): Record<Network, T> => {
  return {
    [Ethereum]: fn(Ethereum),
    [Solana]: fn(Solana),
    [Ton]: fn(Ton),
    [Base]: fn(Base),
    [Bsc]: fn(Bsc),
  }
}

export const toArray = <A, B>(
  record: Record<Network, A>,
  fn: (value: A, network: Network) => B,
): Array<B> => networks.map((network) => fn(record[network], network))

const USDT_ETH = PairAddress('0x0d4a11d5eeaac28ec3f61d100daf4d40471f1852')
const USDC_SOL = PairAddress('58oQChx4yWmvKdwLLZzBi4ChoCc2fqCUWBkwMihLYQo2')
const USDT_TON_DEDUST = PairAddress(
  'EQA-X_yo3fzzbDbJ_0bzFWKqtRuZFIRa1sJsveZJ1YpViO3r',
)
const USDT_BNB = PairAddress('0x16b9a82891338f9bA80E2D6970FddA79D1eb0daE')

// TODO: Recheck for EVM networks
export const mainPairByNetwork: Record<Network, PairAddress> = {
  [Ethereum]: USDT_ETH,
  [Solana]: USDC_SOL,
  [Ton]: USDT_TON_DEDUST,
  [Base]: USDT_ETH,
  [Bsc]: USDT_BNB,
}

export const mainUsdPair = (network: Network) => mainPairByNetwork[network]

export const ENABLED_NETWORKS = [Solana, Ethereum, Bsc] as const

const isTrendingEnabledMap: Record<Network, boolean> = {
  [Solana]: true,
  [Ethereum]: false,
  [Bsc]: false,
  [Ton]: false,
  [Base]: false,
}

export const isTrendingEnabled = (network: Network): boolean => {
  return isTrendingEnabledMap[network]
}

const labelByNetwork: Record<Network, string> = {
  [Solana]: 'Solana',
  [Ethereum]: 'Ethereum',
  [Bsc]: 'BSC',
  [Ton]: 'Ton',
  [Base]: 'Base',
}

export const label = (network: Network) => {
  return labelByNetwork[network]
}
